// Column helpers shared by the grid, the column picker and the export modal.
//
// Column objects are `{ key, title }` where `key` is the Orcanos field `Name`
// (or one of the synthetic `__…` keys) and `title` is the header text.

/** Lowercase, collapse spaces/underscores, trim. The canonical column-name form. */
export function normalizeColKey(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[\s_]+/g, ' ')
    .trim();
}

// ─── Category field ────────────────────────────────────────────────────────
// Used by "Expand by category". Roots (PRT) and children (PI) come from
// different filters and can name the field differently ("Category",
// "PI_Category", "Part Category"), so we match exactly first and only then
// fall back to a contains match.

/** Returns the Field object holding the category, or null. */
export function findCategoryField(fields) {
  const list = Array.isArray(fields) ? fields : [];
  const exact = list.find(
    (f) => normalizeColKey(f.Title) === 'category' || normalizeColKey(f.Name) === 'category'
  );
  if (exact) return exact;
  return (
    list.find(
      (f) =>
        normalizeColKey(f.Title).includes('category') ||
        normalizeColKey(f.Name).includes('category')
    ) || null
  );
}

/**
 * Find the category field across a set of rows — the first row that has one
 * wins. Returns `{ name, title }` (the lookup keys, not the Field object) so
 * callers can read it off any row via byName/byTitle.
 */
export function findCategoryColumn(rows) {
  for (const row of rows || []) {
    const f = findCategoryField(row?.fields);
    if (f) return { name: f.Name || '', title: f.Title || f.Name || 'Category' };
  }
  return null;
}

/** Label used for rows whose category is blank. */
export const NO_CATEGORY = '(No category)';

/** The row's category as plain text, HTML stripped. NO_CATEGORY when blank. */
export function categoryOf(row, col) {
  if (!row || !col) return NO_CATEGORY;
  const raw =
    (col.name && row.byName?.(col.name)) ||
    (col.title && row.byTitle?.(col.title)) ||
    '';
  const text = String(raw).replace(/<[^>]+>/g, '').trim();
  return text || NO_CATEGORY;
}

// ─── Unified columns across work item types ────────────────────────────────
// Under the assembly structure one expand returns rows of THREE work item
// types — the sub-assembly type, PRT parts and DMS items — and each type's
// filter SELECTs its own set of fields. Deriving the grid's columns from the
// first root row alone therefore drew a grid that could not show a single
// field a child type does not share with the root: a DMS item's own columns
// simply did not exist, and every cell of a shared-but-differently-NAMED
// field came back blank (`byName('Obj_name')` against a DMS row whose field
// is named `DMS_name`).
//
// So columns are the UNION of every loaded row's fields, and two fields are
// the SAME column when either their Name or their Title matches (normalized).
// A merged column keeps every alias it was built from, and `fieldTextFor`
// tries them in turn — so "Name" renders for a part and a DMS item alike.

/**
 * Merge the Field arrays of many rows into one ordered column list.
 *
 * Order is first-seen: the first row contributes its fields in their own
 * order, each later row appends only what is new. Passing the roots first
 * therefore leaves a single-type tenant's column order exactly as it was.
 *
 * `skip(name, title)` (both normalized) drops a field before it is merged.
 *
 * Returns `[{ key, title, names, titles }]` — `key`/`title` from the first
 * field that created the column (so an existing saved column preference and
 * every `col.key` consumer keep working), `names`/`titles` the aliases.
 */
export function mergeRowColumns(fieldArrays, { skip } = {}) {
  const columns = [];
  const index = new Map(); // normalized name or title -> column

  for (const fields of fieldArrays || []) {
    if (!Array.isArray(fields)) continue;
    // Within ONE row every field is a distinct column by definition: only
    // fields from earlier rows may be merged into, or a row that names one
    // field the way it titles another would lose a column.
    const createdHere = new Set();
    for (const f of fields) {
      const n = normalizeColKey(f?.Name);
      const t = normalizeColKey(f?.Title);
      if (!n && !t) continue;
      if (skip && skip(n, t)) continue;

      const hit =
        (n && index.get(n)) || (t && index.get(t)) || null;
      if (hit && !createdHere.has(hit)) {
        if (f.Name && !hit.names.includes(f.Name)) hit.names.push(f.Name);
        if (f.Title && !hit.titles.includes(f.Title)) hit.titles.push(f.Title);
        if (n && !index.has(n)) index.set(n, hit);
        if (t && !index.has(t)) index.set(t, hit);
        continue;
      }
      // `hit` was created by THIS row: a distinct field of the same row, which
      // gets its own column — the index keeps pointing at the first one, so the
      // alias only ever resolves to the field that claimed it.

      const col = {
        key: f.Name || f.Title,
        title: f.Title || f.Name,
        names: f.Name ? [f.Name] : [],
        titles: f.Title ? [f.Title] : [],
      };
      columns.push(col);
      createdHere.add(col);
      if (n && !index.has(n)) index.set(n, col);
      if (t && !index.has(t)) index.set(t, col);
    }
  }
  return columns;
}

/**
 * A row's text for a merged column — every alias tried, Names first.
 * Falls back to the column's own key/title for synthetic or legacy columns
 * that carry no alias list.
 */
export function fieldTextFor(row, col) {
  if (!row || !col) return '';
  const names = col.names?.length ? col.names : [col.key];
  for (const n of names) {
    const v = n && row.byName?.(n);
    if (v) return v;
  }
  const titles = col.titles?.length ? col.titles : [col.title];
  for (const t of titles) {
    const v = t && row.byTitle?.(t);
    if (v) return v;
  }
  return '';
}
